/**
 * Agent Hub — Knowledge Tab
 *
 * Training data management scoped to this chatbot.
 * Redesigned using shadcn Card / Dialog / Badge / Table patterns.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  Database,
  Search,
  Trash2,
  RefreshCw,
  FileText,
  Globe,
  Type,
  BarChart3,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Loader2,
  Eye,
  MoreHorizontal,
  XCircle,
  RotateCcw,
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Badge } from '../../../components/ui/Badge'
import { Card, CardContent } from '../../../components/ui/Card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../../components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/ShadcnDropdownMenu'
import { Checkbox } from '../../../components/ui/checkbox'
import { ModernUploadInterface } from '../../../components/features/upload/ModernUploadInterface'
import { useToast } from '../../../hooks/useToast'
import type { Chatbot } from '../../../types'
import { cn } from '../../../utils/cn'
import { api } from '../../../services/api'

// ── Interfaces ────────────────────────────
interface DataSource {
  id: string
  filename: string
  original_filename: string
  file_size?: number
  upload_date: string
  processing_status: 'pending' | 'processing' | 'completed' | 'failed'
  messages_processed: number
  chunks_created: number
  chunks_stored: number
  source_type: 'File' | 'URL' | 'Text'
  ai_suggested_fields?: Record<string, { description: string; extraction_method: string; data_type: string; example_values?: string[] }>
  chunking_strategy?: 'individual' | 'semantic_groups'
  data_type?: string
  isPlaceholder?: boolean
}

interface UploadStatusSnapshot {
  pending: number
  processing: number
  completed: number
  failed: number
  is_active: boolean
  files?: Array<any>
}

type FilterType = 'all' | 'file' | 'url' | 'text'
type StatusFilter = 'all' | 'pending' | 'processing' | 'completed' | 'failed'

const inferSourceTypeFromUpload = (file: any): DataSource['source_type'] => {
  const sessionType = (file?.session_type || '').toLowerCase()
  if (sessionType.includes('url')) return 'URL'
  if (sessionType.includes('text')) return 'Text'
  if (file?.source_type) {
    const src = String(file.source_type).toLowerCase()
    if (src.includes('url')) return 'URL'
    if (src.includes('text')) return 'Text'
  }
  return 'File'
}

// ── Component ─────────────────────────────
interface KnowledgeTabProps {
  chatbot: Chatbot
}

export const KnowledgeTab: React.FC<KnowledgeTabProps> = ({ chatbot }) => {
  const [dataSources, setDataSources] = useState<DataSource[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [activeUploads, setActiveUploads] = useState<DataSource[]>([])
  const [uploadRefreshKey, setUploadRefreshKey] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 8

  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; dataSource: DataSource | null; isDeleting: boolean }>({
    isOpen: false, dataSource: null, isDeleting: false,
  })

  const [chunkModal, setChunkModal] = useState<{ isOpen: boolean; dataSource: DataSource | null; loading: boolean; chunks: any[]; error?: string; expandedMeta: Set<number> }>({
    isOpen: false, dataSource: null, loading: false, chunks: [], expandedMeta: new Set(),
  })

  const [addMetadataState, setAddMetadataState] = useState<{ isOpen: boolean; dataSource: DataSource | null; selectedFields: Set<string>; isAdding: boolean }>({
    isOpen: false, dataSource: null, selectedFields: new Set(), isAdding: false,
  })

  const { toast } = useToast()
  const wasProcessingRef = useRef(false)

  // ── Data loading ──
  const loadDataSources = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await api.get(`/chatbots/${chatbot.id}/data-sources`)
      const fetched = response.data.data_sources || []
      setDataSources(fetched)
      setActiveUploads(prev => prev.filter(placeholder => {
        const key = (placeholder.original_filename || placeholder.filename || '').toLowerCase()
        if (!key) return false
        const matching = fetched.find((ds: DataSource) => (ds.original_filename || ds.filename || '').toLowerCase() === key)
        if (!matching) return true
        return matching.processing_status === 'pending' || matching.processing_status === 'processing'
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data sources')
    } finally {
      setIsLoading(false)
    }
  }, [chatbot.id])

  useEffect(() => { loadDataSources() }, [loadDataSources])
  useEffect(() => { setCurrentPage(1) }, [searchQuery, filterType, statusFilter])

  // ── Upload handlers ──
  const handleUploadComplete = async () => {
    await loadDataSources()
    setTimeout(() => loadDataSources(), 2500)
  }

  const handleStatusUpdate = useCallback((status: UploadStatusSnapshot | null) => {
    if (!status || !Array.isArray(status.files)) {
      if (wasProcessingRef.current) { wasProcessingRef.current = false; loadDataSources() }
      setActiveUploads([])
      return
    }
    const hasActive = status.pending > 0 || status.processing > 0
    if (wasProcessingRef.current && !hasActive) { wasProcessingRef.current = false; loadDataSources() }
    if (hasActive) wasProcessingRef.current = true
    const placeholders: DataSource[] = status.files
      .filter((file: any) => {
        if (file?.data_source_id) return false
        const state = String(file?.status || '').toLowerCase()
        return state === 'pending' || state === 'processing'
      })
      .map((file: any, index: number) => ({
        id: `upload-${file?.session_id || file?.upload_session_id || file?.id || file?.filename || index}`,
        filename: file?.filename || `Processing item ${index + 1}`,
        original_filename: file?.original_filename || file?.filename || `Processing item ${index + 1}`,
        file_size: file?.file_size || file?.size || 0,
        upload_date: file?.created_at || file?.started_at || new Date().toISOString(),
        processing_status: (String(file?.status || 'processing').toLowerCase() === 'pending' ? 'pending' : 'processing') as 'pending' | 'processing',
        messages_processed: file?.messages_processed ?? 0,
        chunks_created: file?.chunks_created ?? 0,
        chunks_stored: file?.chunks_stored ?? 0,
        source_type: inferSourceTypeFromUpload(file),
        isPlaceholder: true,
      }))
    setActiveUploads(placeholders)
  }, [loadDataSources])

  // ── Cancel processing ──
  const cancelProcessing = async (ds: DataSource) => {
    try {
      await api.post(`/data-sources/${ds.id}/cancel`)
      setDataSources(prev => prev.map(d => d.id === ds.id ? { ...d, processing_status: 'failed' } : d))
      setActiveUploads([])
      setUploadRefreshKey(k => k + 1)
      loadDataSources()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel processing')
    }
  }

  // ── Retry failed ──
  const retryProcessing = async (ds: DataSource) => {
    try {
      await api.post(`/data-sources/${ds.id}/retry`)
      setDataSources(prev => prev.map(d => d.id === ds.id ? { ...d, processing_status: 'processing', chunks_stored: 0 } : d))
      setUploadRefreshKey(k => k + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry processing')
    }
  }

  // ── Delete ──
  const confirmDelete = async () => {
    const { dataSource } = deleteConfirm
    if (!dataSource) return
    try {
      setDeleteConfirm(prev => ({ ...prev, isDeleting: true }))
      await api.delete(`/data-sources/${dataSource.id}`)
      setDataSources(prev => prev.filter(ds => ds.id !== dataSource.id))
      setDeleteConfirm({ isOpen: false, dataSource: null, isDeleting: false })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete data source')
      setDeleteConfirm(prev => ({ ...prev, isDeleting: false }))
    }
  }

  // ── Chunk viewer ──
  const openChunkModal = async (dataSource: DataSource) => {
    setChunkModal({ isOpen: true, dataSource, loading: true, chunks: [], expandedMeta: new Set() })
    try {
      const response = await api.get(`/data-sources/${dataSource.id}/chunks`)
      setChunkModal(prev => ({ ...prev, loading: false, chunks: response.data.chunks }))
    } catch {
      setChunkModal(prev => ({ ...prev, loading: false, error: 'Failed to load chunks' }))
    }
  }

  const toggleChunkMeta = (idx: number) => {
    setChunkModal(prev => {
      const next = new Set(prev.expandedMeta)
      if (next.has(idx)) next.delete(idx); else next.add(idx)
      return { ...prev, expandedMeta: next }
    })
  }

  // ── AI metadata ──
  const showAddMetadataDialog = (dataSource: DataSource) => {
    const existingFields = new Set((chatbot as any)?.metadata_schema?.fields?.map((f: any) => f.name) || [])
    const availableFields = Object.keys(dataSource.ai_suggested_fields || {}).filter(n => !existingFields.has(n))
    setAddMetadataState({ isOpen: true, dataSource, selectedFields: new Set(availableFields), isAdding: false })
  }

  const confirmAddMetadata = async () => {
    const { dataSource, selectedFields } = addMetadataState
    if (!dataSource || selectedFields.size === 0) return
    try {
      setAddMetadataState(prev => ({ ...prev, isAdding: true }))
      await api.post(`/chatbots/${chatbot.id}/add-ai-metadata`, { data_source_id: dataSource.id, selected_fields: Array.from(selectedFields) })
      setAddMetadataState({ isOpen: false, dataSource: null, selectedFields: new Set(), isAdding: false })
      toast({ title: 'Fields Added', description: `Added ${selectedFields.size} metadata field(s)`, variant: 'success' })
    } catch (err) {
      toast({ title: 'Failed to Add Fields', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' })
      setAddMetadataState(prev => ({ ...prev, isAdding: false }))
    }
  }

  // ── Filtering / Pagination ──
  const combinedDataSources = useMemo(() => {
    if (activeUploads.length === 0) return dataSources
    const existingIds = new Set(dataSources.map(ds => ds.id))
    const realByName = new Map(dataSources.map(ds => [(ds.original_filename || ds.filename || '').toLowerCase(), ds]))
    const unique = activeUploads.filter(u => {
      if (existingIds.has(u.id)) return false
      const key = (u.original_filename || u.filename || '').toLowerCase()
      return !key || !realByName.get(key)
    })
    return [...unique, ...dataSources]
  }, [activeUploads, dataSources])

  const filteredDataSources = useMemo(() => {
    let filtered = combinedDataSources
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(ds => ds.filename.toLowerCase().includes(q) || ds.original_filename.toLowerCase().includes(q))
    }
    if (filterType !== 'all') filtered = filtered.filter(ds => ds.source_type.toLowerCase() === filterType)
    if (statusFilter !== 'all') filtered = filtered.filter(ds => ds.processing_status === statusFilter)
    return filtered
  }, [combinedDataSources, searchQuery, filterType, statusFilter])

  const totalPages = Math.ceil(filteredDataSources.length / itemsPerPage)
  const paginatedDataSources = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return filteredDataSources.slice(start, start + itemsPerPage)
  }, [filteredDataSources, currentPage])

  const analytics = useMemo(() => {
    const ds = combinedDataSources
    return {
      total: ds.length,
      completed: ds.filter(d => d.processing_status === 'completed').length,
      failed: ds.filter(d => d.processing_status === 'failed').length,
      totalChunks: ds.reduce((s, d) => s + (d.chunks_stored || 0), 0),
    }
  }, [combinedDataSources])

  // ── Helpers ──
  const formatSize = (bytes?: number) => {
    if (!bytes) return null
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }
  const typeIcon = (type: string) => type === 'File' ? <FileText className="h-4 w-4" /> : type === 'URL' ? <Globe className="h-4 w-4" /> : <Type className="h-4 w-4" />
  const statusDot = (status: string) => status === 'completed' ? 'bg-green-500' : status === 'processing' ? 'bg-primary animate-pulse' : status === 'pending' ? 'bg-amber-400' : 'bg-destructive'
  const statusLabel = (status: string) => status === 'completed' ? 'Completed' : status === 'processing' ? 'Processing' : status === 'pending' ? 'Pending' : 'Failed'

  // ── Stat cards config ──
  const statCards = [
    { label: 'Sources', value: analytics.total, icon: FileText, iconClass: 'text-primary bg-primary/10' },
    { label: 'Completed', value: analytics.completed, icon: CheckCircle, iconClass: 'text-green-600 bg-green-500/10 dark:text-green-400' },
    { label: 'Failed', value: analytics.failed, icon: AlertCircle, iconClass: 'text-red-600 bg-red-500/10 dark:text-red-400' },
    { label: 'Chunks', value: analytics.totalChunks.toLocaleString(), icon: BarChart3, iconClass: 'text-violet-600 bg-violet-500/10 dark:text-violet-400' },
  ]

  return (
    <div className="space-y-6">
      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map(stat => (
          <Card key={stat.label}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', stat.iconClass)}>
                  <stat.icon className="h-4 w-4" />
                </div>
              </div>
              <p className="text-3xl font-bold tracking-tight text-foreground">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Upload section ── */}
      <ModernUploadInterface
        selectedChatbot={chatbot}
        onUploadComplete={handleUploadComplete}
        onStatusUpdate={handleStatusUpdate}
        refreshKey={uploadRefreshKey}
      />

      {/* ── Search & filters ── */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Search files, URLs, or content..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="h-10 pl-10 bg-card"
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {/* Type filter */}
            <div className="flex items-center gap-0.5 rounded-lg bg-muted p-1">
              {([{ value: 'all', label: 'All' }, { value: 'file', label: 'Files' }, { value: 'url', label: 'URLs' }, { value: 'text', label: 'Text' }] as const).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilterType(opt.value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                    filterType === opt.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-0.5 rounded-lg bg-muted p-1">
              {([{ value: 'all', label: 'All Status' }, { value: 'completed', label: 'Completed' }, { value: 'failed', label: 'Failed' }] as const).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setStatusFilter(opt.value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                    statusFilter === opt.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <Button variant="outline" size="icon" onClick={() => loadDataSources()} disabled={isLoading}>
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* ── Data source list ── */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading training data...</p>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-2">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm font-medium text-foreground">Failed to load</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            <Button variant="link" size="sm" onClick={() => loadDataSources()} className="mt-1">
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : filteredDataSources.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <Database className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">
                {combinedDataSources.length === 0 ? 'No training data yet' : 'No matches found'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {combinedDataSources.length === 0 ? 'Upload files, URLs, or Q&A to get started.' : 'Try adjusting your search or filters.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {filteredDataSources.length} source{filteredDataSources.length !== 1 ? 's' : ''}
            </span>
            {totalPages > 1 && (
              <span className="text-xs text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>

          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[45%]">Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Chunks</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedDataSources.map(ds => {
                  const aiFields = ds.ai_suggested_fields || {}
                  const existingFieldNames = (chatbot as any)?.metadata_schema?.fields?.map((f: any) => f.name) || []
                  const availableFields = Object.entries(aiFields).filter(([name]) => !existingFieldNames.includes(name))
                  const allFieldsAdded = Object.keys(aiFields).length > 0 && availableFields.length === 0

                  return (
                    <TableRow key={ds.id} className="group">
                      {/* Name + type icon + AI fields */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                            {typeIcon(ds.source_type)}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {ds.original_filename || ds.filename}
                            </p>
                            {/* AI-suggested field tags */}
                            {availableFields.length > 0 && (
                              <div className="mt-1 flex items-center gap-1.5">
                                <span className="text-xs font-medium text-primary">AI fields</span>
                                {availableFields.slice(0, 3).map(([name]) => (
                                  <Badge key={name} variant="outline" className="text-xs font-normal px-1.5 py-0">
                                    {name}
                                  </Badge>
                                ))}
                                {availableFields.length > 3 && (
                                  <span className="text-xs text-muted-foreground">+{availableFields.length - 3}</span>
                                )}
                              </div>
                            )}
                            {allFieldsAdded && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3 w-3" />
                                <span>All fields added</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Size */}
                      <TableCell className="text-sm text-muted-foreground">
                        {formatSize(ds.file_size) || '—'}
                      </TableCell>

                      {/* Date */}
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(ds.upload_date).toLocaleDateString()}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        {ds.isPlaceholder ? (
                          <div className="flex items-center gap-1.5">
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                            <span className="text-xs text-muted-foreground">Processing</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <div className={cn('h-2 w-2 rounded-full', statusDot(ds.processing_status))} />
                            <span className="text-xs text-muted-foreground">{statusLabel(ds.processing_status)}</span>
                          </div>
                        )}
                      </TableCell>

                      {/* Chunks */}
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{ds.chunks_stored}</span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        {!ds.isPlaceholder && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {(ds.processing_status === 'processing' || ds.processing_status === 'pending') && (
                                <DropdownMenuItem onClick={() => cancelProcessing(ds)} className="text-amber-600 focus:text-amber-600">
                                  <XCircle className="h-4 w-4" />
                                  <span>Cancel Processing</span>
                                </DropdownMenuItem>
                              )}
                              {ds.processing_status === 'failed' && (
                                <DropdownMenuItem onClick={() => retryProcessing(ds)}>
                                  <RotateCcw className="h-4 w-4" />
                                  <span>Retry</span>
                                </DropdownMenuItem>
                              )}
                              {ds.chunks_stored > 0 && (
                                <DropdownMenuItem onClick={() => openChunkModal(ds)}>
                                  <Eye className="h-4 w-4" />
                                  <span>View Chunks</span>
                                </DropdownMenuItem>
                              )}
                              {availableFields.length > 0 && (
                                <DropdownMenuItem onClick={() => showAddMetadataDialog(ds)}>
                                  <BarChart3 className="h-4 w-4" />
                                  <span>Add AI Fields</span>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDeleteConfirm({ isOpen: true, dataSource: ds, isDeleting: false })}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4))
                const page = pageStart + i
                if (page > totalPages) return null
                return (
                  <Button
                    key={page}
                    variant={page === currentPage ? 'default' : 'ghost'}
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                )
              }).filter(Boolean)}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Delete confirmation (AlertDialog) ── */}
      <AlertDialog open={deleteConfirm.isOpen} onOpenChange={open => { if (!open) setDeleteConfirm({ isOpen: false, dataSource: null, isDeleting: false }) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Training Data</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm.dataSource?.original_filename || deleteConfirm.dataSource?.filename}"?
              This action cannot be undone. All processed chunks will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteConfirm.isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteConfirm.isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteConfirm.isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Chunk viewer (Dialog) ── */}
      <Dialog open={chunkModal.isOpen} onOpenChange={open => { if (!open) setChunkModal(prev => ({ ...prev, isOpen: false })) }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="truncate">
              {chunkModal.dataSource?.original_filename || chunkModal.dataSource?.filename}
            </DialogTitle>
            <DialogDescription>
              {chunkModal.chunks.length} chunk{chunkModal.chunks.length !== 1 ? 's' : ''} stored
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {chunkModal.loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Loading chunks...</span>
              </div>
            ) : chunkModal.error ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <span className="text-sm text-destructive">{chunkModal.error}</span>
              </div>
            ) : chunkModal.chunks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Database className="h-6 w-6 text-muted-foreground/50" />
                <span className="text-sm text-muted-foreground">No chunks found</span>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {chunkModal.chunks.map((chunk: any, idx: number) => {
                  const meta = chunk.metadata || {}
                  const isMetaOpen = chunkModal.expandedMeta.has(idx)
                  const metaEntries = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
                  return (
                    <div key={chunk.id || idx} className="py-4">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0 space-y-2">
                          {meta.title && (
                            <p className="text-sm font-semibold text-foreground">{meta.title}</p>
                          )}
                          <div className="whitespace-pre-wrap break-words rounded-lg border border-border bg-muted px-4 py-3 text-sm leading-relaxed text-muted-foreground">
                            {chunk.content}
                          </div>
                          {metaEntries.length > 0 && (
                            <button
                              onClick={() => toggleChunkMeta(idx)}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', isMetaOpen && 'rotate-90')} />
                              Metadata ({metaEntries.length})
                            </button>
                          )}
                          {isMetaOpen && metaEntries.length > 0 && (
                            <div className="rounded-lg border border-border overflow-hidden">
                              <table className="w-full text-xs">
                                <tbody className="divide-y divide-border">
                                  {metaEntries.map(([key, value]) => (
                                    <tr key={key} className="hover:bg-muted/50">
                                      <td className="bg-muted/50 px-3 py-2 font-medium text-muted-foreground whitespace-nowrap align-top w-40">
                                        {key}
                                      </td>
                                      <td className="px-3 py-2 text-foreground break-all">
                                        {Array.isArray(value)
                                          ? (value as any[]).map((v, i) => (
                                              <Badge key={i} variant="outline" className="mr-1 text-xs font-normal">
                                                {String(v)}
                                              </Badge>
                                            ))
                                          : String(value)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── AI Metadata dialog ── */}
      <Dialog open={addMetadataState.isOpen} onOpenChange={open => { if (!open) setAddMetadataState({ isOpen: false, dataSource: null, selectedFields: new Set(), isAdding: false }) }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Add AI-Suggested Fields</DialogTitle>
            <DialogDescription>Select fields to add to your metadata configuration.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3 py-2">
            {addMetadataState.dataSource && Object.entries(addMetadataState.dataSource.ai_suggested_fields || {})
              .filter(([name]) => !((chatbot as any)?.metadata_schema?.fields?.map((f: any) => f.name) || []).includes(name))
              .map(([fieldName, fieldInfo]) => (
                <div
                  key={fieldName}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-all',
                    addMetadataState.selectedFields.has(fieldName)
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border hover:border-border/80 hover:bg-muted/50',
                  )}
                  onClick={() => {
                    setAddMetadataState(prev => {
                      const next = new Set(prev.selectedFields)
                      if (next.has(fieldName)) next.delete(fieldName); else next.add(fieldName)
                      return { ...prev, selectedFields: next }
                    })
                  }}
                >
                  <Checkbox
                    checked={addMetadataState.selectedFields.has(fieldName)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground">{fieldName}</span>
                      <Badge variant="outline" className="text-xs font-normal">{fieldInfo.data_type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{fieldInfo.description}</p>
                    {fieldInfo.example_values && fieldInfo.example_values.length > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        <span className="font-medium">Examples: </span>
                        <code className="rounded bg-muted px-1.5 py-0.5">{fieldInfo.example_values.slice(0, 3).join(', ')}</code>
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <span className="text-sm text-muted-foreground">{addMetadataState.selectedFields.size} field(s) selected</span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setAddMetadataState({ isOpen: false, dataSource: null, selectedFields: new Set(), isAdding: false })}
                disabled={addMetadataState.isAdding}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmAddMetadata}
                disabled={addMetadataState.selectedFields.size === 0 || addMetadataState.isAdding}
              >
                {addMetadataState.isAdding ? 'Adding...' : 'Add to Metadata'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default KnowledgeTab
